import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db, functions } from '../firebase';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export const AuthContext = createContext();

// Kolay erişim için useAuth hook'u
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Firestore'dan kullanıcı bilgilerini al (tek sorgu)
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          const userData = userDocSnap.exists() ? userDocSnap.data() : {};
          const isAdmin = userData.admin === true;
          
          const userWithProfile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            name: userData.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0],
            admin: isAdmin,
            ...userData,
          };
          
          setUser(userWithProfile);
          setIsAuthenticated(true);
        } catch (err) {
          console.error('Kullanıcı bilgisi alınırken hata:', err);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
          });
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });
    return () => {
      unsubscribe();
    }
  }, []);

  const signup = async (email, password) => {
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      
      // Resend ile verification code gönder
      try {
        const sendCodeFn = httpsCallable(functions, 'sendVerificationCode');
        await sendCodeFn({ email });
      } catch (verifyErr) {
        console.error('Verification code gönderilemedi:', verifyErr);
      }
      
      // Kullanıcıyı çıkış yaptır (email doğrulanana kadar giriş yapamaz)
      await signOut(auth);
      
      return { success: true, user: cred.user, email };
    } catch (err) {
      console.error('Signup hatası:', err.code, err.message);
      throw err;
    }
  };

  const sendVerificationCode = async (email) => {
    try {
      const sendCodeFn = httpsCallable(functions, 'sendVerificationCode');
      await sendCodeFn({ email });
      return { success: true };
    } catch (err) {
      console.error('Verification code gönderme hatası:', err);
      throw err;
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const verifyFn = httpsCallable(functions, 'verifyEmailCode');
      const result = await verifyFn({ email, code });
      return result.data;
    } catch (err) {
      console.error('Verification hatası:', err);
      throw err;
    }
  };

  const checkEmailVerified = async (email) => {
    try {
      const checkFn = httpsCallable(functions, 'checkEmailVerified');
      const result = await checkFn({ email });
      return result.data.verified;
    } catch (err) {
      console.error('Email verification kontrolü hatası:', err);
      return false;
    }
  };

  const login = async (email, password) => {
    setError(null);
    
    // Önce email'in doğrulanmış olup olmadığını kontrol et
    const isVerified = await checkEmailVerified(email);
    if (!isVerified) {
      const err = new Error('Email adresiniz doğrulanmamış. Lütfen doğrulama kodunu girin.');
      err.code = 'auth/email-not-verified';
      throw err;
    }
    
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error("Çıkış yaparken hata oluştu:", err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated, 
        loading,
        error,
        login, 
        signup,
        logout,
        sendVerificationCode,
        verifyCode,
        checkEmailVerified,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};